import type { AlertEventType, AlertRule, AlertSource, AlertSourceKind } from '../types/gotypes'

/**
 * What an alert watches (Adam, 11 Oct 2026): a saved table, a question about
 * the job's data (the agent builds a `pt query` crosstab), or something that
 * happens on the job (no script, no model). The kind is fixed at create.
 */
export const ALERT_SOURCE_KINDS: { value: AlertSourceKind; label: string; chip: string }[] = [
  { value: 'table', label: 'A saved table', chip: 'Table' },
  { value: 'query', label: "A question about the job's data", chip: 'Question' },
  { value: 'event', label: 'Something that happens', chip: 'Event' },
]

/** The event types an event alert may watch, in the order goapi's AlertEventTypes lists them. */
export const ALERT_EVENT_TYPES: { value: AlertEventType; label: string }[] = [
  { value: 'job_import_completed', label: 'New data imported into the job' },
  { value: 'job_import_failed', label: 'A data import failed' },
  { value: 'import_batch_failed', label: 'A social import batch failed' },
  { value: 'import_batch_truncated', label: 'A social import batch was cut short' },
]

/** The two import-batch events, the only ones a pipeline narrows (AlertEventTypeWatchesBatches). */
export const isBatchEvent = (type: string | undefined): boolean =>
  type === 'import_batch_failed' || type === 'import_batch_truncated'

export const kindChipLabel = (kind: AlertSourceKind): string =>
  ALERT_SOURCE_KINDS.find((k) => k.value === kind)?.chip ?? 'Table'

export const eventLabel = (type: string | undefined): string =>
  ALERT_EVENT_TYPES.find((e) => e.value === type)?.label ?? type ?? ''

/**
 * The rule's source: config.source, or for a rule saved before source kinds
 * existed, a table source on its first table's job. Mirrors
 * AlertRule.EffectiveSource in goapi/pkg/types/alerts.go.
 */
export function alertSourceOf(alert: AlertRule | undefined): AlertSource {
  const firstJob = alert?.tables?.[0]?.job ?? ''
  const src = alert?.config?.source
  if (src?.kind) {
    return src.kind === 'table' && !src.job ? { ...src, job: firstJob } : src
  }
  return { kind: 'table', job: firstJob }
}
